import React, { useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSystemIntel } from '../api/intel';
import { searchSystems, SystemSearchResult } from '../api/systems';
import { FONT } from '../theme/tokens';

/** Shared system-name search — the Dashboard hero (members) and the public
 * system page's tab bar (everyone) both mount it, so a system lookup is
 * reachable with or without a login. Prefetches the highlighted option's
 * intel so the click itself feels instant. `onHighlight` (optional) reports
 * the currently-highlighted option so a caller can surface that same
 * prefetch as a live preview — see Dashboard's Hero. */
const SystemSearchBox: React.FC<{
  onSelect: (s: SystemSearchResult) => void;
  onHighlight?: (s: SystemSearchResult | null) => void;
  placeholder?: string;
  // Accepts a responsive object so a caller sharing a row with other
  // controls can hand it the full line on narrow screens instead of
  // letting a fixed px width crowd them.
  width?: number | string | Record<string, number | string>;
}> = ({ onSelect, onHighlight, placeholder = 'Open a system…', width = 260 }) => {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const { data } = useQuery({
    queryKey: ['system-search', q],
    queryFn: () => searchSystems(q),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });

  return (
    <Autocomplete
      size="small"
      options={data ?? []}
      getOptionLabel={(o) => o.name}
      onInputChange={(_, v) => setQ(v)}
      onChange={(_, v) => v && onSelect(v)}
      onHighlightChange={(_, option) => {
        onHighlight?.(option);
        if (option) {
          queryClient.prefetchQuery({ queryKey: ['intel', 'system', option.solar_system_id], queryFn: () => fetchSystemIntel(option.solar_system_id) });
        }
      }}
      onClose={() => onHighlight?.(null)}
      sx={{ width }}
      renderInput={(params) => <TextField {...params} placeholder={placeholder} sx={{ fontFamily: FONT.mono }} />}
    />
  );
};

export default SystemSearchBox;
